import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { StripePaymentComponent } from '@pedix-workspace/angular-ui-payment';
import { ActivatedRoute, Router } from '@angular/router';
import { RegistrationSharedService } from '../registration-shared.service';
import { DxValidationGroupComponent, DxValidationGroupModule } from 'devextreme-angular/ui/validation-group';
import { CardModel } from '@pedix-workspace/angular-ui-payment';
import { AnalyticsService } from '../../shared/analytics.service';
import { RegistrationService } from '../registration.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { BillingType, StripePromotionCodeSuccess, ZenriseBankAccount } from '@pedix-workspace/utils';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { ArgentinePaymentComponent } from './argentine-payment/argentine-payment.component';
import { ArgentineCbuPaymentComponent } from '@pedix-workspace/angular-ui-payment';
import { StripeCouponCodeComponent } from '@pedix-workspace/angular-ui-payment';

import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-step-subscription',
    templateUrl: './step-subscription.component.html',
    styleUrls: ['./step-subscription.component.scss'],
    imports: [
        TranslocoDirective,
        DxValidationGroupModule,
        StripePaymentComponent,
        StripeCouponCodeComponent,
        ArgentinePaymentComponent,
        DxCheckBoxModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        ArgentineCbuPaymentComponent,
        ButtonComponent
    ]
})
export class StepSubscriptionComponent implements OnInit {
  loading = false;
  showSubscriptionDetails = false;
  apiUrl = environment.apiUrl;
  billingType: BillingType;
  promotionCode: string;
  paymentMethodType: 'card' | 'cbu' = 'card';

  @ViewChild('stripePayment')
  stripePayment: StripePaymentComponent;

  @ViewChild('validationGroup')
  validationGroup: DxValidationGroupComponent;

  cardModel: CardModel = {
    cardNumber: '',
    cardName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    dni: '',
    creditCard: null,
    documentType: undefined,
  };

  cbuModel: ZenriseBankAccount = {
    holderName: '',
    cbuNumber: '',
    holderIdNumber: '',
    bankId: null,
  };

  private toastrService = inject(ToastrService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);
  public registrationSharedService = inject(RegistrationSharedService);
  private registrationService = inject(RegistrationService);
  readonly banks = this.registrationService.banks;
  private t = inject(TranslocoService);
  
  constructor() {}

  ngOnInit(): void {
    this.billingType = this.registrationSharedService.registrationData.billingType;
    this.promotionCode = this.registrationSharedService.promotionCode;
    this.analyticsService.startedCheckout();
  }

  get countryCode() {
    return this.registrationSharedService.registrationData.establishment.countryCode;
  }

  get customerFullName() {
    return `${this.registrationSharedService.registrationData.customer.firstName} ${this.registrationSharedService.registrationData.customer.lastName}`;
  }

  async processPayment() {
    this.loading = true;

    if (this.countryCode === 'AR') {
      await this.processArgentinePayment();
      return;
    } else {
      await this.processInternationalPayment();
      return;
    }
  }

  private async processArgentinePayment() {
    if (!this.validationGroup.instance.validate().isValid) {
      this.toastrService.error(this.t.translate('general.error'));
      this.loading = false;
      return;
    }

    this.analyticsService.addedPaymentInfo();

    try {
      if (this.paymentMethodType === 'cbu') {
        await this.registrationService.createArgentineCbuSubscription(this.cbuModel);
      } else {
        await this.registrationService.createArgentineSubscription(this.cardModel);
      }
      this.redirectToSuccess();
    } catch ({ errorTitle, errorMessage }) {
      this.toastrService.error(errorMessage, errorTitle);
      this.loading = false;
    }
  }

  private async processInternationalPayment() {
    if (!this.validationGroup.instance.validate().isValid) {
      this.toastrService.error(this.t.translate('general.error'));
      this.loading = false;
      return;
    }

    try {
      this.analyticsService.addedPaymentInfo();
      await this.registrationService.createInternationalSubscription(this.stripePayment);
      this.redirectToSuccess();
    } catch ({ errorTitle, errorMessage }) {
      this.toastrService.error(errorMessage, errorTitle);
      this.loading = false;
    }
  }

  onSelectCbu() {
    this.paymentMethodType = 'cbu';
    this.registrationService.loadBanks();
  }

  handleCouponSuccess(stripePromotionCodeSuccess: StripePromotionCodeSuccess | null) {
    this.registrationSharedService.registrationData.couponId = stripePromotionCodeSuccess?.couponId;
    this.registrationSharedService.registrationData.promotionCode = stripePromotionCodeSuccess?.promotionCode;
  }

  handleCouponError(error: string) {
    this.toastrService.error(error, this.t.translate('general.errorTitle'));
  }

  private redirectToSuccess() {
    this.router.navigate(['../success'], {
      relativeTo: this.activatedRoute,
    });
    this.loading = false;
  }

  prev() {
    this.analyticsService.previousStep('account');
    this.router.navigate(['../account'], { relativeTo: this.activatedRoute });
  }
}
